import { checkOSCRNumber, useCreateNewAccount } from "@/api/authQueries"
import { Link } from "react-router-dom"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { APP_ROUTES } from "@/common/urls"
import { CheckCheck, Loader, Info } from "lucide-react"
import { Timestamp } from "firebase/firestore"
import { Checkbox } from "@/components/ui/checkbox"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import toast from "react-hot-toast"
import { useState } from "react"
import {
    countryOptions,
    countryTelCodes,
    RegisterFormSchema,
} from "./RegisterForm.schema"
import { Separator } from "@/components/ui/separator"

export default function AuthRegister() {
    const [isCharityNumberPending, setIsCharityNumberPending] = useState(false)
    const {
        mutateAsync: createAccount,
        isPending,
        isSuccess,
    } = useCreateNewAccount()

    const form = useForm<z.infer<typeof RegisterFormSchema>>({
        resolver: zodResolver(RegisterFormSchema),
        defaultValues: {
            email: "",
            firstName: "",
            lastName: "",
            mobileNumber: "",
            secondaryFirstName: "",
            secondaryLastName: "",
            secondaryEmail: "",
            password: "",
            confirmPassword: "",
            country: "",
            charityNumber: "",
            acceptTCs: false,
        },
    })

    async function onSubmit(values: z.infer<typeof RegisterFormSchema>) {
        setIsCharityNumberPending(true)
        try {
            // check if charity number is already in use
            const isCharityNumberInUse = await checkOSCRNumber(
                values.charityNumber
            )

            if (isCharityNumberInUse) {
                toast.error("Charity number is already in use")
                return
            }

            await createAccount({
                role: "applicant",
                dateCreated: Timestamp.now(),
                totalPastGrantAmount: null,
                userId: "",
                ...values,
            })
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : "Unable to validate charity number"
            toast.error(message)
        } finally {
            setIsCharityNumberPending(false)
        }
    }

    const [country] = form.watch(["country"])

    const charityNumberPlaceholder = (() => {
        switch (country) {
            case "canada":
                return "12345 6789 RR0001"
            case "scotland":
                return "SC012345"
        }
        return "Charity Number / OSCR Number"
    })()

    return (
        <div className="bg-center bg-no-repeat bg-auth-pattern flex justify-center items-center pb-20 pt-8">
            <div className="bg-white shadow-lg border-input max-w-[645px] sm:px-32 px-4 rounded-lg py-10 gap-6 flex flex-col justify-center items-center">
                <section className="flex items-center justify-center">
                    <img src="/CMHT_logo_large.svg" alt="CMHT Logo" />
                </section>

                <section className="flex flex-col gap-2 items-center">
                    <h1 className="text-2xl text-app-danger font-bold">
                        Create an Account
                    </h1>
                    <p className="text-sm">
                        Select the country in which your organisation operates.
                        If you don&apos;t see what you&apos;re looking for,
                        unfortunately we don&apos;t operate in your country.
                    </p>
                </section>
                <section className="flex flex-col w-full">
                    <Form {...form}>
                        <form
                            onSubmit={form.handleSubmit(onSubmit)}
                            className="flex flex-col gap-6"
                        >
                            <FormField
                                control={form.control}
                                name="country"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Country</FormLabel>
                                        <FormControl>
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <SelectTrigger className="rounded-lg border-black">
                                                    <SelectValue placeholder="Country" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {countryOptions.map(
                                                        (country) => (
                                                            <SelectItem
                                                                key={country}
                                                                value={country}
                                                            >
                                                                <span className="capitalize">
                                                                    {country}
                                                                </span>
                                                            </SelectItem>
                                                        )
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="charityNumber"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Charity Number / OSCR Number
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                className="rounded-lg border-black"
                                                placeholder={
                                                    charityNumberPlaceholder
                                                }
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem className="flex flex-col">
                                        <FormLabel>Email Address</FormLabel>
                                        <FormControl>
                                            <Input
                                                className="rounded-lg border-black"
                                                placeholder="Email"
                                                type="email"
                                                {...field}
                                            />
                                        </FormControl>
                                        <div className="flex items-start gap-2">
                                            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                                            <p className="text-xs leading-relaxed text-muted-foreground">
                                                Disclaimer: Use a shared or
                                                generic email address accessible
                                                to your team (e.g. info@,
                                                admin@).
                                            </p>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Password</FormLabel>
                                        <FormControl>
                                            <Input
                                                className="rounded-lg border-black"
                                                type="password"
                                                placeholder="Password"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="confirmPassword"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Confirm Password</FormLabel>
                                        <FormControl>
                                            <Input
                                                className="rounded-lg border-black"
                                                type="password"
                                                placeholder="Password"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>First Name</FormLabel>
                                        <FormControl>
                                            <Input
                                                className="rounded-lg border-black"
                                                placeholder="First Name"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="lastName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Last Name</FormLabel>
                                        <FormControl>
                                            <Input
                                                className="rounded-lg border-black"
                                                placeholder="Last Name"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="mobileNumber"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Mobile Number</FormLabel>
                                        <div className="relative">
                                            <p className="absolute -translate-y-1/2 top-1/2 left-4 text-sm text-black z-10">
                                                {countryTelCodes.find(
                                                    (c) => c.country === country
                                                )?.codes[0] || "+"}
                                            </p>
                                            <FormControl>
                                                <Input
                                                    className="pl-12 rounded-lg border-black"
                                                    placeholder="555-555-111"
                                                    {...field}
                                                    onBlur={() => {
                                                        // sideffect: remove leading 0s
                                                        field.onChange({
                                                            target: {
                                                                value: field.value.replace(
                                                                    /^0+/,
                                                                    ""
                                                                ),
                                                            },
                                                        })
                                                        field.onBlur()
                                                    }}
                                                />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <Separator />
                            <FormField
                                control={form.control}
                                name="secondaryFirstName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Secondary First Name
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                className="rounded-lg border-black"
                                                placeholder="Secondary First Name"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="secondaryLastName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Secondary Last Name
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                className="rounded-lg border-black"
                                                placeholder="Secondary Last Name"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="secondaryEmail"
                                render={({ field }) => (
                                    <FormItem className="flex flex-col">
                                        <FormLabel>
                                            Secondary Email Address
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                className="rounded-lg border-black"
                                                placeholder="secondary@example.com"
                                                type="email"
                                                {...field}
                                            />
                                        </FormControl>
                                        <div className="flex items-start gap-2">
                                            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                                            <p className="text-xs leading-relaxed text-muted-foreground">
                                                Must belong to a different
                                                authorised person. This cannot
                                                be another email owned by the
                                                same individual
                                            </p>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="acceptTCs"
                                render={({ field }) => (
                                    <FormItem className="flex flex-col justify-start">
                                        <FormControl>
                                            <div className="flex items-center space-x-2">
                                                <Checkbox
                                                    checked={field.value}
                                                    onCheckedChange={
                                                        field.onChange
                                                    }
                                                    id="terms"
                                                />
                                                <label
                                                    htmlFor="terms"
                                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                                >
                                                    Accept{" "}
                                                    <a
                                                        href="https://www.christinamaryhendrietrust.co.uk/terms-and-conditions"
                                                        referrerPolicy="no-referrer"
                                                        className="text-app-defaultLight underline font-bold"
                                                    >
                                                        terms and conditions
                                                    </a>
                                                </label>
                                            </div>
                                        </FormControl>

                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button
                                size="lg"
                                type="submit"
                                className="flex justify-center items-center gap-4 disabled:cursor-not-allowed"
                                disabled={isPending || isCharityNumberPending}
                            >
                                <span>Submit</span>
                                {isPending ||
                                    (isCharityNumberPending && (
                                        <Loader
                                            size={24}
                                            className="animate-spin"
                                        />
                                    ))}
                                {isSuccess && (
                                    <CheckCheck
                                        size={24}
                                        className="animate-pulse"
                                    />
                                )}
                            </Button>
                        </form>
                    </Form>
                </section>
                <section className="text-sm">
                    <span>Already a user?</span>{" "}
                    <Link
                        className="text-app-defaultLight underline font-bold"
                        to={APP_ROUTES.AUTH.LOGIN}
                    >
                        Log in here
                    </Link>
                </section>
            </div>
        </div>
    )
}
